import { BaseLogo, getLogoOrgId } from "../../../../core/models/entities/BaseLogo";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { ErrorView } from "../../../components/error-view/ErrorView";
import LoadingElement from "../../../components/loading-element/loading-element";
import { LogoDetails } from "../components/logo-details.component";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../../shared/hooks/useComponentStateMachine";
import { useNavigate } from "react-router";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import { performCavemanReFetch } from "../../../../utils/functions/caveman-utils";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { useQueryClient } from "@tanstack/react-query";
import { withToasts } from "../../../components/Toast/Toast";

export function LogoDetailsWrapper({
  logo,
  onViewRejectNote,
  closeDialog,
}: {
  logo: BaseLogo;
  onViewRejectNote: (logo: BaseLogo) => void;
  closeDialog: () => void;
}) {
  const navigate = useNavigate();
  const { logoRepository } = useRepositoriesContext();
  const { profile } = useProfileContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const {
    data: logoDetails,
    isLoading: isSummaryLoading,
    isSuccess,
    isError,
  } = logoRepository.useLogo(getLogoOrgId(logo), logo.id);

  const {
    handleGoToEditLogo,
    handleDeleteLogo,
    handleChangeTitle,
    handlePlaceVerdict,
    handleAddToCart,
    handleCopyThenGoToLogo,
    handleSkipApproval,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    isLoading: isActionRunning,
    handleRejectAndDelete,
  } = useLogoActionHandler();
  const { state, dataTransition } = useComponentStateMachine();

  const isLoading = isSummaryLoading || isActionRunning;
  dataTransition(isLoading, isSuccess, isError);

  function handleViewDesign(logo: BaseLogo) {
    navigate(
      `/design?pageSize=25&pageIndex=1&filterBy=designlogos.logoid&filterOn=${logo.id}`
    );
  }

  function handleCreateDesign(logo: BaseLogo) {
    const params = new URLSearchParams({
      addLogoId: String(logo.id),
      logoTitle: logo.title,
    });
    navigate(`/design/create?${params.toString()}`);
  }

  function handleTitle(title: string) {
    handleChangeTitle(getLogoOrgId(logo), logo.id, title);
  }

  async function onDelete(logo: BaseLogo) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    try {
      await handleDeleteLogo(logo);
      closeDialog();
    } catch (error) {
      console.error("Failed to delete logo:", error);
    }
  }

  function onSendApproval(logo: BaseLogo, email: string) {
    // Use conditional approval for Customers to preserve existing approval statuses
    // Use regular approval for Vendors who have full control
    if (profile?.userOrganizationInformations.role === Role.EndUser) {
      handleSendApprovalWithEndCustomerPending(logo, email).then(
        performCavemanReFetch
      );
    } else {
      handleSendApproval(logo, email);
    }
  }

  function onRejectAndDelete(logo: BaseLogo, note?: string | undefined) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handleRejectAndDelete(logo, note);
  }

  function onPlaceVerdict(logo: BaseLogo, verdict: Verdict) {
    // No need for performCavemanReFetch - LogoRepository now handles optimistic updates
    return handlePlaceVerdict(logo, verdict);
  }

  async function handleAssignCustomer(customerId: number) {
    const transaction = async () => {
      await apiClient.assignCustomer(getLogoOrgId(logo), logo.id, customerId);
      await queryClient.invalidateQueries(["logos"]);
      await queryClient.invalidateQueries(["logos", logo.id]);
    };

    await withToasts(
      transaction,
      "Assigning customer...",
      "Customer assigned successfully",
      "Failed to assign customer"
    );
  }

  return state === ComponentState.Succeed ? (
    <LogoDetails
      onPlaceVerdict={onPlaceVerdict}
      logo={logoDetails!}
      onAddToCart={handleAddToCart}
      onShowRejectionNote={onViewRejectNote}
      onDeleteLogo={onDelete}
      onTitleChange={handleTitle}
      onViewDesigns={handleViewDesign}
      onEditLogo={handleGoToEditLogo}
      onCopyLogo={handleCopyThenGoToLogo}
      onSkipApproval={handleSkipApproval}
      onSendApproval={onSendApproval}
      isLoading={isLoading}
      onRejectAndDelete={onRejectAndDelete}
      onAssignCustomer={handleAssignCustomer}
      onCreateDesign={handleCreateDesign}
    />
  ) : state === ComponentState.Loading ? (
    <div className={"w-220 h-128 relative"}>
      <LoadingElement />
    </div>
  ) : (
    <div className={"w-220 h-128 relative"}>
      <ErrorView />
    </div>
  );
}
